import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Form,
  InputGroup,
  Button,
  Row,
  Col,
  Card,
  Badge,
} from "react-bootstrap";
import { useLang } from "../../contexts/LanguageContext";
import apiUrls from "../../api/apiUrls";
import fetchWithAuth from "../../api/fetchWithAuth";
import { languageMeta } from "../../translations";
import "./ListContainer.css";
import Book from "../Book";

const buildLanguageMaps = () => {
  const labelMap = {};
  const code3Map = {};

  Object.entries(languageMeta).forEach(([code2, meta]) => {
    if (!meta) {
      return;
    }

    const label = meta.label;
    const normalizedCode2 =
      typeof code2 === "string" ? code2.toLowerCase() : undefined;
    const normalizedCode3 =
      typeof meta.code3 === "string" ? meta.code3.toLowerCase() : normalizedCode2;

    if (label) {
      if (normalizedCode2) {
        labelMap[normalizedCode2] = label;
      }
      if (normalizedCode3) {
        labelMap[normalizedCode3] = label;
      }
    }

    if (normalizedCode2 && normalizedCode3) {
      code3Map[normalizedCode2] = normalizedCode3;
      code3Map[normalizedCode3] = normalizedCode3;
    }

    if (Array.isArray(meta.aliases)) {
      meta.aliases.forEach((alias) => {
        if (typeof alias !== "string") {
          return;
        }
        const normalizedAlias = alias.toLowerCase();
        if (label) {
          labelMap[normalizedAlias] = label;
        }
        if (normalizedCode3) {
          code3Map[normalizedAlias] = normalizedCode3;
        }
      });
    }
  });

  return { labelMap, code3Map };
};

const { labelMap: languageLabelMap, code3Map: languageCodeToCode3Map } = buildLanguageMaps();

const normalizeLanguageCode = (code) => {
  if (!code) {
    return "";
  }
  const normalizedCode = String(code).toLowerCase();
  return languageCodeToCode3Map[normalizedCode] ?? normalizedCode;
};

const deriveComparableId = (book) =>
  (book?.book_id ?? book?.id ?? "").toString();

const ListContainer = () => {
  const { t } = useLang();
  const [searchTerm, setSearchTerm] = useState("");
  const [languageFilter, setLanguageFilter] = useState("all");
  const [sortOption, setSortOption] = useState("recent");
  const [statusFilter, setStatusFilter] = useState("all");
  const [readingList, setReadingList] = useState([]);
  const [rawResponse, setRawResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingRemovalId, setPendingRemovalId] = useState(null);
  const [actionFeedback, setActionFeedback] = useState(null);

  const normalizeStatusValue = useCallback((status) => {
    if (!status) return "queued";
    const normalized = String(status).toLowerCase();
    if (normalized === "not_started") return "queued";
    if (normalized === "queued") return "queued";
    if (normalized === "in_progress" || normalized === "reading") return "in_progress";
    if (normalized === "finished" || normalized === "done") return "finished";
    return normalized;
  }, []);

  const extractBooks = useCallback((payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.results)) return payload.results;
    if (Array.isArray(payload?.books)) return payload.books;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.reading_list)) return payload.reading_list;
    if (Array.isArray(payload?.readingList)) return payload.readingList;
    if (Array.isArray(payload?.data?.reading_list)) return payload.data.reading_list;
    if (Array.isArray(payload?.data?.readingList)) return payload.data.readingList;
    return [];
  }, []);

  const normalizeBook = useCallback(
    (entry, index) => {
      const candidate = entry?.book ?? entry ?? {};
      const tagsCandidate = candidate.tags ?? entry?.tags;
      const rawStatus = entry?.status ?? candidate.status ?? "queued";
      const rawLanguage =
        candidate.book_language ?? candidate.language ?? candidate.lang ?? entry?.language ?? "";
      const language = normalizeLanguageCode(rawLanguage);

      return {
        id: entry?.id ?? candidate.book_id ?? candidate.id ?? `book-${index}`,
        title: candidate.book_title ?? candidate.title ?? "",
        author: candidate.book_author ?? candidate.author ?? "",
        language,
        book_language: language,
        status: normalizeStatusValue(rawStatus),
        progress:
          typeof entry?.progress === "number"
            ? entry.progress
            : typeof candidate.progress === "number"
            ? candidate.progress
            : 0,
        lastOpened:
          entry?.lastOpened ??
          entry?.last_opened ??
          entry?.updated_at ??
          candidate.lastOpened ??
          candidate.last_opened ??
          candidate.updated_at ??
          "",
        tags: Array.isArray(tagsCandidate) ? tagsCandidate : [],
      };
    },
    [normalizeStatusValue]
  );

  const fetchReadingList = useCallback(
    async ({ signal, preserveFeedback = false } = {}) => {
      if (!preserveFeedback) {
        setActionFeedback(null);
      }
      setIsLoading(true);
      try {
        const response = await fetchWithAuth(apiUrls.readingList, {
          signal,
        });

        if (!response.ok) {
          const rawMessage = await response.text();
          throw new Error(rawMessage || `HTTP ${response.status}`);
        }

        const payload = await response.json();
        setRawResponse(payload);

        const rawBooks = extractBooks(payload);
        const normalizedBooks = rawBooks.map(normalizeBook);
        setReadingList(normalizedBooks);
        setError(null);
      } catch (err) {
        if (err.name === "AbortError") {
          return;
        }
        console.error("Error fetching reading list:", err);
        setError(t("LIST_ERROR"));
      } finally {
        if (!signal || !signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [extractBooks, normalizeBook, t]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchReadingList({ signal: controller.signal });

    return () => {
      controller.abort();
    };
  }, [fetchReadingList]);

  useEffect(() => {
    const handleRefresh = () => {
      fetchReadingList({ preserveFeedback: true });
    };

    window.addEventListener("reading-list-updated", handleRefresh);

    return () => {
      window.removeEventListener("reading-list-updated", handleRefresh);
    };
  }, [fetchReadingList]);

  const filteredBooks = useMemo(() => {
    const baseBooks = readingList;
    const normalizedSearch = searchTerm.trim().toLowerCase();
    let books = baseBooks.map((book) => {
      const normalizedLanguage = normalizeLanguageCode(
        book.language ?? book.book_language
      );
      const resolvedLanguage =
        normalizedLanguage || book.book_language || book.language || "";

      return {
        ...book,
        status: normalizeStatusValue(book.status),
        language: resolvedLanguage,
        book_language: resolvedLanguage,
      };
    });

    const statusOrder = {
      in_progress: 0,
      queued: 1,
      finished: 2,
    };

    if (languageFilter !== "all") {
      books = books.filter((book) => book.language === languageFilter);
    }

    if (statusFilter !== "all") {
      const targetStatus = normalizeStatusValue(statusFilter);
      books = books.filter((book) => book.status === targetStatus);
    }

    if (normalizedSearch) {
      books = books.filter(
        (book) =>
          (book.title || "").toLowerCase().includes(normalizedSearch) ||
          (book.author || "").toLowerCase().includes(normalizedSearch)
      );
    }

    const sorters = {
      recent: (a, b) => (a.lastOpened < b.lastOpened ? 1 : -1),
      title: (a, b) => a.title.localeCompare(b.title),
      author: (a, b) => a.author.localeCompare(b.author),
      status: (a, b) => {
        const statusDiff =
          (statusOrder[a.status] ?? Number.MAX_SAFE_INTEGER) -
          (statusOrder[b.status] ?? Number.MAX_SAFE_INTEGER);
        if (statusDiff !== 0) {
          return statusDiff;
        }
        return a.title.localeCompare(b.title);
      },
    };

    return books.sort(sorters[sortOption] ?? sorters.recent);
  }, [
    languageFilter,
    normalizeStatusValue,
    readingList,
    searchTerm,
    sortOption,
    statusFilter,
  ]);

  const fallbackCollection = readingList;
  const featuredBook = filteredBooks[0] ?? fallbackCollection[0];
  const stats = useMemo(() => {
    const collection = fallbackCollection.map((book) => ({
      ...book,
      status: normalizeStatusValue(book.status),
    }));
    const saved = collection.length;
    const queued = collection.filter((book) => book.status === "queued").length;
    const inProgress = collection.filter((book) => book.status === "in_progress").length;
    const finished = collection.filter((book) => book.status === "finished").length;

    return { saved, queued, inProgress, finished };
  }, [fallbackCollection, normalizeStatusValue]);

  const renderStatusLabel = (status) => {
    const statusMap = {
      queued: t("LIST_STATUS_QUEUED"),
      in_progress: t("LIST_STATUS_IN_PROGRESS"),
      finished: t("LIST_STATUS_FINISHED"),      
      not_started: t("LIST_STATUS_QUEUED"),
    };

    return statusMap[status] || status;
  };

  const renderLanguageLabel = (code) => {
    if (!code) {
      return t("SEARCH_RESULT_LANGUAGE_UNKNOWN");
    }

    const normalizedCode = String(code).toLowerCase();
    const label = languageLabelMap[normalizedCode];

    if (label) {
      return label;
    }

    return typeof code === "string"
      ? code.toUpperCase()
      : t("SEARCH_RESULT_LANGUAGE_UNKNOWN");
  };

  const handleRemoveBook = useCallback(
    async (bookId) => {
      const resolvedId = bookId;
      if (!resolvedId) {
        setActionFeedback({ type: "error", message: t("LIST_REMOVE_ERROR") });
        return;
      }

      setActionFeedback(null);
      setPendingRemovalId(resolvedId);

      try {
        console.log("readingList:delete:request", {
          endpoint: apiUrls.readingListItem(resolvedId),
          method: "DELETE",
        });
        const response = await fetchWithAuth(apiUrls.readingListItem(resolvedId), {
          method: "DELETE",
        });

        console.log("readingList:delete:response", {
          status: response.status,
          ok: response.ok,
        });

        if (!response.ok) {
          const rawMessage = await response.text();
          console.log("readingList:delete:error-body", rawMessage);
          throw new Error(rawMessage || `HTTP ${response.status}`);
        }
        const responseBody = await response.text();
        if (responseBody) {
          try {
            console.log("readingList:delete:payload", JSON.parse(responseBody));
          } catch {
            console.log("readingList:delete:payload", responseBody);
          }
        }

        setReadingList((prev) =>
          prev.filter(
            (book) => deriveComparableId(book) !== resolvedId
          )
        );
        setActionFeedback({ type: "success", message: t("LIST_REMOVE_SUCCESS") });
      } catch (err) {
        console.error("Error removing book from reading list:", err);
        setActionFeedback({ type: "error", message: t("LIST_REMOVE_ERROR") });
      } finally {
        setPendingRemovalId(null);
      }
    },
    [t]
  );

  return (
    <Container className="py-4">
        <div className="list-container">
        <h2>{t('LIST_TITLE')}</h2>
        <p className="text-muted mb-4">{t("LIST_INTRO", { TOTAL : stats.saved}) }</p>

        {isLoading && (
          <p className="text-muted small">{t("LIST_LOADING")}</p>
        )}
        {error && (
          <p className="text-danger small mb-4">{error}</p>
        )}
        {actionFeedback && (
          <div
            className={`alert ${
              actionFeedback.type === "error" ? "alert-danger" : "alert-success"
            } py-2 small`}
            role="alert"
          >
            {actionFeedback.message}
          </div>
        )}

        <Row className="g-3 mb-4">
          {/*
          <Col md={4}>
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <Card.Title className="h6 text-uppercase text-muted mb-2">
                  {t("LIST_SUMMARY_SAVED")}
                </Card.Title>
                <Card.Text className="display-6 fw-semibold mb-0">{stats.saved}</Card.Text>
                <small className="text-muted">{t("LIST_SUMMARY_SAVED_HINT")}</small>
              </Card.Body>
            </Card>
          </Col>
          */}
          <Col md={4}>
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <Card.Title className="h6 text-uppercase text-muted mb-2">
                  {t("LIST_SUMMARY_QUEUED")}
                </Card.Title>
                <Card.Text className="display-6 fw-semibold mb-0">{stats.queued}</Card.Text>
                <small className="text-muted">{t("LIST_SUMMARY_QUEUED_HINT")}</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <Card.Title className="h6 text-uppercase text-muted mb-2">
                  {t("LIST_SUMMARY_IN_PROGRESS")}
                </Card.Title>
                <Card.Text className="display-6 fw-semibold mb-0">{stats.inProgress}</Card.Text>
                <small className="text-muted">{t("LIST_SUMMARY_IN_PROGRESS_HINT")}</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <Card.Title className="h6 text-uppercase text-muted mb-2">
                  {t("LIST_SUMMARY_FINISHED")}
                </Card.Title>
                <Card.Text className="display-6 fw-semibold mb-0">{stats.finished}</Card.Text>
                <small className="text-muted">{t("LIST_SUMMARY_FINISHED_HINT")}</small>
              </Card.Body>
            </Card>
          </Col>
          
        </Row>

        <section className="mb-5">
          <h3 className="h5 mb-3">{t("LIST_FILTER_TITLE")}</h3>
          <Row className="g-3 align-items-end">
            <Col xs={12} md={6} lg={6}>
              <Form.Group controlId="listSearch">
                <Form.Label className="small text-uppercase text-muted fw-semibold">
                  {t("LIST_FILTER_SEARCH_LABEL")}
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="search"
                    placeholder={t("LIST_FILTER_SEARCH_PLACEHOLDER")}
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                  <Button variant="primary">{t("LIST_FILTER_SEARCH_BUTTON")}</Button>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={3}>
              <Form.Group controlId="listLanguage">
                <Form.Label className="small text-uppercase text-muted fw-semibold">
                  {t("LIST_FILTER_LANGUAGE_LABEL")}
                </Form.Label>
                <Form.Select
                  value={languageFilter}
                  onChange={(event) => setLanguageFilter(event.target.value)}
                >
                  <option value="all">{t("LIST_FILTER_LANGUAGE_ALL")}</option>
                  {Object.entries(languageMeta).map(([code, meta]) => (
                    <option value={meta.code3} key={meta.code3}>
                      {meta.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={3}>
              <Form.Group controlId="listStatus">
                <Form.Label className="small text-uppercase text-muted fw-semibold">
                  {t("LIST_FILTER_STATUS_LABEL")}
                </Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="all">{t("LIST_FILTER_STATUS_ALL")}</option>
                  <option value="queued">{t("LIST_FILTER_STATUS_QUEUED")}</option>
                  <option value="in_progress">{t("LIST_FILTER_STATUS_IN_PROGRESS")}</option>
                  <option value="finished">{t("LIST_FILTER_STATUS_FINISHED")}</option>
                </Form.Select>
              </Form.Group>
            </Col>
            {/*
            <Col xs={12} md={6} lg={3}>
              <Form.Group controlId="listSort">
                <Form.Label className="small text-uppercase text-muted fw-semibold">
                  {t("LIST_FILTER_SORT_LABEL")}
                </Form.Label>
                <Form.Select
                  value={sortOption}
                  onChange={(event) => setSortOption(event.target.value)}
                >
                  <option value="recent">{t("LIST_FILTER_SORT_RECENT")}</option>
                  <option value="title">{t("LIST_FILTER_SORT_TITLE")}</option>
                  <option value="author">{t("LIST_FILTER_SORT_AUTHOR")}</option>
                  <option value="status">{t("LIST_FILTER_SORT_STATUS")}</option>
                </Form.Select>
              </Form.Group>
            </Col>
            */}
          </Row>
        </section>
        
        {/*
        {featuredBook && (
          <section className="mb-5">
            <h3 className="h5 mb-3">{t("LIST_FEATURED_TITLE")}</h3>
            <Card className="border-0 shadow-sm">
              <Card.Body className="d-flex flex-column flex-md-row gap-3 align-items-start">
                <div className="flex-grow-1">
                  <Card.Subtitle className="text-muted mb-1">
                    {t("LIST_FEATURED_SUBTITLE", { DATE: featuredBook.lastOpened })}
                  </Card.Subtitle>
                  <Card.Title className="h4">{featuredBook.title}</Card.Title>
                  <p className="mb-2">{featuredBook.author}</p>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <Badge bg="info" text="dark">
                      {renderLanguageLabel(featuredBook.language)}
                    </Badge>
                    <Badge bg="secondary">{renderStatusLabel(featuredBook.status)}</Badge>
                    {featuredBook.tags.map((tag) => (
                      <Badge bg="light" text="dark" key={tag}>
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <div className="d-flex gap-2">
                    <Button variant="primary">{t("LIST_CARD_CONTINUE")}</Button>
                    <Button variant="outline-secondary">{t("LIST_CARD_REMOVE")}</Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </section>
        )}
        */}

        <section>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="h5 mb-0">{t("LIST_BOOKS_SECTION_TITLE")}</h3>
            <span className="text-muted small">
              {t("LIST_BOOKS_SECTION_COUNT", { COUNT: filteredBooks.length })}
            </span>
          </div>

          {filteredBooks.length === 0 ? (
            <Card className="border-0 shadow-sm text-center py-5">
              <Card.Body>
                <Card.Title className="h5 mb-3">{t("LIST_EMPTY_TITLE")}</Card.Title>
                <Card.Text className="text-muted mb-0">{t("LIST_EMPTY_SUBTITLE")}</Card.Text>
              </Card.Body>
            </Card>
          ) : (
          <Row className="g-4">
            {filteredBooks.map((book, index) => {
              const coverId = book.book_id ?? book.id ?? `book-${index}`;
              const bookcover = {
                book_id: coverId,
                book_title: book.book_title ?? book.title ?? t("SEARCH_RESULT_UNTITLED"),
                book_author: book.book_author ?? book.author ?? "",
                book_language: book.book_language ?? book.language ?? "",
              };

              return (
                <Col md={6} lg={4} key={coverId}>
                  <Card className="h-100 shadow-sm ">
                    <Card.Body className="d-flex flex-column">
                      <div className="cover-link">
                        <Book book={bookcover} />
                      </div>
                      <Card.Title>{book.title || t("SEARCH_RESULT_UNTITLED")}</Card.Title>
                      {(book.author || book.book_author) && (
                        <Card.Subtitle className="mb-2 text-muted">
                          {book.author ?? book.book_author}
                        </Card.Subtitle>
                      )}
                      <div className="small text-muted mb-3">
                        <div>
                          {renderLanguageLabel(
                            book.language ?? book.book_language ?? bookcover.book_language
                          )}
                        </div>
                        <div>{renderStatusLabel(book.status)}</div>
                      </div>
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        {book.tags.map((tag) => (
                          <Badge bg="light" text="dark" key={tag}>
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <div className="mt-auto">
                        <Button
                          variant="link"
                          className="p-0"
                          disabled={pendingRemovalId === coverId}
                          onClick={() => handleRemoveBook(coverId)}
                        >
                          {pendingRemovalId === coverId
                            ? t("LIST_CARD_REMOVING")
                            : t("LIST_CARD_REMOVE")}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </section>
      </div>
    </Container>
  );
};

export default ListContainer;
